/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master/LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Visionware.

*/

/**
  This file is used for controlling the global states of the components,
  you can customize the states for the different components here.
*/

import { createContext, useContext, useReducer, useEffect } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// The Vision UI Dashboard Material main context
const VisionUI = createContext();

// Setting custom name for the context which is visible on react dev tools
VisionUI.displayName = "VisionUIContext";

// Local storage key
const STORAGE_KEY = "sarto_dashboard_ui";

// Default state
const defaultState = {
  miniSidenav: false,
  transparentSidenav: true,
  sidenavColor: "primary",
  transparentNavbar: true,
  fixedNavbar: false,
  openConfigurator: false,
  direction: "ltr",
  layout: "dashboard",
};

// Load persisted config
const savedConfig = localStorage.getItem(STORAGE_KEY);

const initialState = savedConfig
  ? {
      ...defaultState,
      ...JSON.parse(savedConfig),
    }
  : defaultState;

// Vision UI Dashboard React reducer
function reducer(state, action) {
  switch (action.type) {
    case "MINI_SIDENAV": {
      return { ...state, miniSidenav: action.value };
    }

    case "TRANSPARENT_SIDENAV": {
      return { ...state, transparentSidenav: action.value };
    }

    case "SIDENAV_COLOR": {
      return { ...state, sidenavColor: action.value };
    }

    case "TRANSPARENT_NAVBAR": {
      return { ...state, transparentNavbar: action.value };
    }

    case "FIXED_NAVBAR": {
      return { ...state, fixedNavbar: action.value };
    }

    case "OPEN_CONFIGURATOR": {
      return { ...state, openConfigurator: action.value };
    }

    case "DIRECTION": {
      return { ...state, direction: action.value };
    }

    case "LAYOUT": {
      return { ...state, layout: action.value };
    }

    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

// Vision UI Dashboard React context provider
function VisionUIControllerProvider({ children }) {
  const [controller, dispatch] = useReducer(reducer, initialState);

  // Persist dashboard UI config
  useEffect(() => {
    const persistedConfig = {
      miniSidenav: controller.miniSidenav,
      transparentSidenav: controller.transparentSidenav,
      sidenavColor: controller.sidenavColor,
      transparentNavbar: controller.transparentNavbar,
      fixedNavbar: controller.fixedNavbar,
      direction: controller.direction,
      layout: controller.layout,
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(persistedConfig));
  }, [
    controller.miniSidenav,
    controller.transparentSidenav,
    controller.sidenavColor,
    controller.transparentNavbar,
    controller.fixedNavbar,
    controller.direction,
    controller.layout,
  ]);

  return <VisionUI.Provider value={[controller, dispatch]}>{children}</VisionUI.Provider>;
}

// Vision UI Dashboard React custom hook for using context
function useVisionUIController() {
  const context = useContext(VisionUI);

  if (!context) {
    throw new Error("useVisionUIController should be used inside the VisionUIControllerProvider.");
  }

  return context;
}

// Typechecking props for the VisionUIControllerProvider
VisionUIControllerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Context module functions
const setMiniSidenav = (dispatch, value) => dispatch({ type: "MINI_SIDENAV", value });

const setTransparentSidenav = (dispatch, value) => dispatch({ type: "TRANSPARENT_SIDENAV", value });

const setSidenavColor = (dispatch, value) => dispatch({ type: "SIDENAV_COLOR", value });

const setTransparentNavbar = (dispatch, value) => dispatch({ type: "TRANSPARENT_NAVBAR", value });

const setFixedNavbar = (dispatch, value) => dispatch({ type: "FIXED_NAVBAR", value });

const setOpenConfigurator = (dispatch, value) => dispatch({ type: "OPEN_CONFIGURATOR", value });

const setDirection = (dispatch, value) => dispatch({ type: "DIRECTION", value });

const setLayout = (dispatch, value) => dispatch({ type: "LAYOUT", value });

export {
  VisionUIControllerProvider,
  useVisionUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
};
